import React, { useCallback, useContext, useEffect } from 'react';

import { FatalAttraction as FA } from '@vpr/tracking';

import { useDebouncedCallback } from '@vimeo/ui/hooks/use-debounced-callback';
import { ViewerContext } from '@vimeo/viewer';

import AlbumConfigContext from '../../containers/AlbumConfigContext';
import { EmbedShowcaseContext } from '../../context/useEmbedShowcase';
import { albumVideosRequestFields, SEARCH_PAGE_SIZE } from '../../utils/constants';
import { useSearchDispatch, useSearchState } from '../SearchContext';
import SearchField from './SearchField';
import { SearchWrapper } from './SearchStyled';

type Props = {
  setShowSearchBar?: (bool: boolean) => void;
  autofocus?: boolean;
  className?: string;
  teamRole?: string;
};

export default function Search({ setShowSearchBar, autofocus = true, className, teamRole }: Props) {
  // this will be replaced with values from global.ts, this is just mocked out for testing.
  const config = useContext(ViewerContext);
  const { albumId, hashedPass } = useContext(AlbumConfigContext);
  const searchDispatch = useSearchDispatch();
  const { searchPage, searchText } = useSearchState();
  const embedShowcaseData = useContext(EmbedShowcaseContext);

  useEffect(() => {
    debouncedSearch(searchText);
  }, [searchPage]);

  const closeSearch = useCallback(() => {
    FA.trackClick({
      container: 'showcase',
      component: 'search',
      keyword: 'close_search',
    });
    if (teamRole) {
      FA.trackClick({
        container: 'team_home_page',
        component: 'search',
        keyword: 'close_search_' + teamRole.toLowerCase(),
      });
    }
    searchDispatch({ type: 'CLOSE_SEARCH' });
  }, [searchDispatch]);

  const openSearch = useCallback(() => {
    searchDispatch({ type: 'START_SEARCH' });
  }, [searchDispatch]);

  /**  Create a bound fetch, we're going to using this for dispatching requests
   * as side effects when we don't want to throw the application into Suspense
   * mode.
   */

  const request = useCallback(
    async (endpoint: string, init: RequestInit = {}) => {
      const { headers = {} } = init;
      FA.trackClick({
        container: 'showcase',
        component: 'search',
        keyword: 'execute_search',
      });
      if (teamRole) {
        FA.trackClick({
          container: 'team_home_page',
          component: 'search',
          keyword: 'execute_search_' + teamRole.toLowerCase(),
        });
      }
      const response = await fetch(
        `//${config && config.apiUrl}${endpoint}&per_page=${SEARCH_PAGE_SIZE}&page=${searchPage}`,
        {
          method: 'GET',
          ...init,
          headers: {
            Authorization: `jwt ${config && config.jwt}`,
            Accept: 'application/vnd.vimeo.*;version=3.4.1',
            'Content-Type': 'application/json',
            ...headers,
          },
        },
      );

      return await response.json();
    },
    [config, searchPage],
  );

  const fields = albumVideosRequestFields;

  const { debouncedCallback: debouncedSearch } = useDebouncedCallback(async (query: string) => {
    if (query.length > 0) {
      try {
        const params = new URLSearchParams({
          query: query,
          _hashed_pass: hashedPass || '',
          fields: fields.join(','),
          referrer: document?.referrer || '',
        });
        const url = `/albums/${albumId}/videos?${params.toString()}&is_embed=${
          embedShowcaseData?.isShowcaseEmbeded
        }`;
        const json = await request(url, {
          headers: {
            Accept: 'application/vnd.vimeo.*+json;version=3.4.1',
          },
        });
        if (json.data && json.data.length > 0) {
          FA.trackEvent({
            container: 'showcase',
            component: 'search',
            keyword: 'results_shown',
          });
        } else {
          FA.trackEvent({
            container: 'showcase',
            component: 'search',
            keyword: 'no_results',
          });
        }
        searchDispatch({ type: 'SEARCH_SUCCESS', payload: json });
      } catch (e) {
        FA.trackEvent({
          container: 'showcase',
          component: 'showcase-api-error',
          keyword: `showcase-api-error: ${e}`,
        });
        searchDispatch({ type: 'SEARCH_ERROR' });
      }
    }
  }, 1000);

  const updateSearch = useCallback(
    (value: string) => {
      if (value.length > 0) {
        openSearch();
        debouncedSearch(value);
        searchDispatch({ type: 'SEARCH_TEXT', payload: value });
      } else {
        closeSearch();
      }
    },
    [searchDispatch],
  );

  return (
    <SearchWrapper className={className} title="Search Field">
      <SearchField
        closeSearch={closeSearch}
        onChange={updateSearch}
        setShowSearchBar={setShowSearchBar}
        autofocus={autofocus}
      />
    </SearchWrapper>
  );
}
